import { atom } from '@tldraw/state';
import { flushSync } from 'react-dom';

/**
 * Shape ids whose React fibers should unmount before the tldraw store record
 * is removed. SafeShapeLayer reads this so hook-using util.component() fibers
 * unmount cleanly instead of re-rendering with a deleted record
 * ("Rendered fewer hooks than expected").
 */
export const pendingShapeUnmountIds = atom('pendingShapeUnmountIds', new Set());

export function isShapePendingUnmount(shapeId) {
  return Boolean(shapeId && pendingShapeUnmountIds.get().has(shapeId));
}

export function markShapesPendingUnmount(ids) {
  if (!ids?.length) return;
  const next = new Set(pendingShapeUnmountIds.get());
  let changed = false;
  for (const id of ids) {
    if (id && !next.has(id)) {
      next.add(id);
      changed = true;
    }
  }
  if (changed) pendingShapeUnmountIds.set(next);
}

export function clearShapesPendingUnmount(ids) {
  if (!ids?.length) return;
  const next = new Set(pendingShapeUnmountIds.get());
  let changed = false;
  for (const id of ids) {
    if (next.delete(id)) changed = true;
  }
  if (changed) pendingShapeUnmountIds.set(next);
}

/**
 * Unmount shape React fibers (via pendingShapeUnmountIds) before mutating the
 * store, so tldraw util hooks are not skipped on a zombie render.
 */
export function unmountShapesThen(ids, fn) {
  const list = [...new Set((ids || []).filter(Boolean))];
  if (!list.length) return fn();

  flushSync(() => {
    markShapesPendingUnmount(list);
  });
  try {
    return fn();
  } finally {
    clearShapesPendingUnmount(list);
  }
}
